import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAvivaSelector } from "@redux/hook";
import { SHIELD_STARTER_HELTH_PLUS } from "@static/staticConstents";
import { SCREEN_SIZE } from "@static/screen-size";
import { Tooltip, setTooltipPosition } from "../Tooltip";

const Chart = styled.div<{ rotationDeg?: string }>`
  height: 130px;
  overflow: hidden;
  & svg {
    max-width: 260px;
    transition: all 0.3s linear;
    transform-origin: center center;
    transform: ${(prop) =>
      prop.rotationDeg ? `rotate(${prop.rotationDeg})` : ""};
    max-height: 260px;
    @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
      max-height: 450px;
      max-width: 450px;
      min-width: 400px;
    }
  }
  @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    height: 230px;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  width: max-content;

  .toolTip {
    position: absolute;
    width: 100%;
    height: 100%;
    #info {
      display: none;
    }
    @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
      &.right {
        .top {
          left: 112px;
        }
      }
      .top {
        left: -16px;
      }
    }
  }

  @media (max-width: ${SCREEN_SIZE.tabletsUp}px) {
    .redTooltip, .greenTooltip {
      .toolTip {
        .top {
          transform: translate(0, 100%);
        }
      }
    }
  }

  &.redChart {
    .redTooltip {
      .toolTip {
        .gray_area {
          @media (max-width: ${SCREEN_SIZE.tabletsUp}px) {
            display: block;
          }
        }
        .top {
          visibility: visible;
          @media (max-width: ${SCREEN_SIZE.tabletsUp}px) {
            transform: translate(0, 0);
          }
        }
      }
    }
  }

  &.greenChart {
    .greenTooltip {
      .toolTip {
        .gray_area {
          @media (max-width: ${SCREEN_SIZE.tabletsUp}px) {
            display: block;
          }
        }
        .top {
          visibility: visible;
          @media (max-width: ${SCREEN_SIZE.tabletsUp}px) {
            transform: translate(0, 0);
          }
        }
      }
    }
  }

  .greenTooltip {
    position: absolute;
    right: -24px;
    top: 64px;
    width: 50px;
    height: 50px;
    @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
      right: -50px;
      top: 120px;
      width: 100px;
      height: 100px;
    }
  }
  
`;

const Info = styled.div`
  position: absolute;
  top: 15px;
  background: #fff;
  width: 50px;
  height: 50px;
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-family: "Open Sans";
  font-weight: 700;
  box-shadow: 0px 1px 20px rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  font-size: 18px;
  color: #fa8f8c;
  z-index: 2;

  @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    width: 100px;
    height: 100px;
    font-size: 34.2857px;
    top: 24px;
  }
`;
const greenInfo = css`
  color: #13a498;
  position: absolute;
  right: -24px;
  top: 64px;
  z-index: 1;
  @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    right: -50px;
    top: 120px;
  }
`;

const InfoLabel = styled.div`
  color: #434343;
  font-style: normal;
  font-weight: 600;
  font-size: 6px;
  line-height: 120%;
  text-align: center;
  margin-top:-5px;
  @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    font-size: 12px;
  }
`;

const greenChartGroup = css`
  &:hover .greenChart {
    filter: drop-shadow(0 0 8px #c1eeea);
  }
`;

const redChartGroup = css`
  &:hover .redChart {
    filter: drop-shadow(0 0 8px #fa8f8c);
  }
`;

const redChart = css`
  &:hover {
    filter: drop-shadow(0 0 8px #fa8f8c);
  }
`;

const chartGroup = css`
  transform: translate(8px, 0px);
`;

export default function DonutChart() {
  const selectedPlan = useAvivaSelector(
    (state) => state.shieldStarterStep2.policyName
  );
  const [rotationRef, setRotationRef] = useState("50deg");
  const redPercentage = useRef<HTMLSpanElement>(null);
  const greenPercentage = useRef<HTMLSpanElement>(null);
  const timer = useRef<any>(null);

  const detectScreenWidth = useCallback(() => {
    setTooltipPosition(window.innerWidth, true);
  }, []);

  useEffect(() => {
    setTooltipPosition(window.innerWidth, true);
    window.addEventListener("scroll", setTooltipPosition);
    window.addEventListener("resize", detectScreenWidth);
    return () => {
      window.removeEventListener("scroll", setTooltipPosition);
      window.removeEventListener("resize", detectScreenWidth);
    };
  }, []);

  useEffect(() => {
    function percentageManipulator() {
      const intervalPeriod = 1.23;
      timer.current = setInterval(() => {
        if (
          selectedPlan === SHIELD_STARTER_HELTH_PLUS &&
          redPercentage.current &&
          +redPercentage.current?.innerHTML < 95
        ) {
          redPercentage.current.innerHTML = (
            +redPercentage.current?.innerHTML + 1
          ).toString();
        } else if (
          selectedPlan !== SHIELD_STARTER_HELTH_PLUS &&
          redPercentage.current &&
          +redPercentage.current?.innerHTML > 58
        ) {
          redPercentage.current.innerHTML = (
            +redPercentage.current?.innerHTML - 1
          ).toString();
        }

        if (
          selectedPlan === SHIELD_STARTER_HELTH_PLUS &&
          greenPercentage.current?.innerHTML &&
          +greenPercentage.current.innerHTML > 5
        ) {
          greenPercentage.current.innerHTML = (
            +greenPercentage.current?.innerHTML - 1
          ).toString();
        } else if (
          selectedPlan !== SHIELD_STARTER_HELTH_PLUS &&
          greenPercentage.current?.innerHTML &&
          +greenPercentage.current.innerHTML < 42
        ) {
          greenPercentage.current.innerHTML = (
            +greenPercentage.current?.innerHTML + 1
          ).toString();
        }

        if (
          selectedPlan === SHIELD_STARTER_HELTH_PLUS &&
          redPercentage.current &&
          +redPercentage.current.innerHTML === 95 &&
          greenPercentage.current &&
          +greenPercentage.current?.innerHTML === 5
        ) {
          //@ts-ignore
          clearInterval(timer.current);
        } else if (
          selectedPlan !== SHIELD_STARTER_HELTH_PLUS &&
          redPercentage.current &&
          +redPercentage.current.innerHTML === 58 &&
          greenPercentage.current &&
          +greenPercentage.current.innerHTML === 42
        ) {
          //@ts-ignore
          clearInterval(timer.current);
        }
      }, intervalPeriod);
    }
    console.log(selectedPlan);
    if (selectedPlan === SHIELD_STARTER_HELTH_PLUS) {
      setRotationRef("30deg");
    } else {
      setRotationRef("0deg");
    }

    percentageManipulator();

    return () => {
      //@ts-ignore
      clearInterval(timer.current);
    };
  }, [selectedPlan]);

  useEffect(() => {
    if (redPercentage.current) {
      redPercentage.current.innerHTML =
        selectedPlan === SHIELD_STARTER_HELTH_PLUS ? "95" : "58";
    }
    if (greenPercentage.current) {
      greenPercentage.current.innerHTML =
        selectedPlan === SHIELD_STARTER_HELTH_PLUS ? "5" : "42";
    }

    const chartimg = document.querySelectorAll(".chartimg");
    const chartContainer: any = document.querySelector(".chartContainer");
    const topEle = chartContainer.querySelectorAll(".top");
    const toolTip = chartContainer.querySelectorAll(".toolTip");
    const RED_CHART = "redChart";
    const GREEN_CHART = "greenChart"
    const MAX_CHILD_ELEMENTS =  3;
     
    chartimg.forEach((el, i) => {
      el.addEventListener("mouseover", (e: any) => {
        chartContainer.classList.add(e.target.classList);
      });
      el.addEventListener("mouseout", (e: any) => {
        chartContainer.classList.remove(e.target.classList);
      });

      el.addEventListener("touchend", (e: any) => {

        let selectedTopEle = 0;
        e.target.classList[0] === GREEN_CHART && (selectedTopEle = 1);
        const grayBgElement = document.createElement("span");
        grayBgElement.className = "gray_area";
        if(toolTip[selectedTopEle].childElementCount < MAX_CHILD_ELEMENTS) {
          toolTip[selectedTopEle].appendChild(grayBgElement)
          const grayArea = toolTip[selectedTopEle].querySelector(".gray_area");
          grayArea.addEventListener("touchend", (e:any)=>{
            selectedTopEle === 1 
            ? chartContainer.classList.remove(GREEN_CHART) 
            : chartContainer.classList.remove(RED_CHART)
          });
        }

        chartContainer.classList.add(e.target.classList);

        topEle[selectedTopEle]?.addEventListener("touchend", (e: any) => {
          selectedTopEle === 1 
          ? chartContainer.classList.remove(GREEN_CHART)
          : chartContainer.classList.remove(RED_CHART)
        });
      });

    });
  }, []);


  return (
    <>
      <ChartContainer className="chartContainer">
        <span className="redTooltip">
          <Tooltip title="What Singlife will pay" isIcon={true}>
            {selectedPlan === SHIELD_STARTER_HELTH_PLUS ? (
              <>
                <p>
                  For preferred medical providers, Singlife will cover up to
                  S$20,000 (subject to benefit limits), less 5% co- payment.
                </p>
                <p>
                  For non-preferred medical provider, Singlife will cover half
                  of the bill, up to S$20,000 (subject to benefit limits), less
                  5% co-payment.
                </p>
              </>
            ) : (
              <>
                <p>
                  For preferred medical providers, Singlife will cover up to
                  S$20,000 (subject to benefit limits), less the deductible and
                  co-insurance.
                </p>
                <p>
                  For non-preferred medical providers, Singlife will cover half
                  of the bill, up to S$20,000 (subject to benefit limits), less
                  the deductible and co-insurance.
                </p>
              </>
            )}
          </Tooltip>
        </span>
        <span className="greenTooltip">
          <Tooltip title="What you will pay" isIcon={true}>
            {selectedPlan === SHIELD_STARTER_HELTH_PLUS ? (
              <>
                <p>
                  Co-payment (5% of total bill) <br/> This is the fixed percentage of
                  costs you have to pay regardless of your hospital bill size.
                </p>
              </>
            ) : (
              <>
                <p>
                  1) Deductible (maximum S$3,500/year) <br/> The deductible is the
                  amount you have to pay per policy year before Singlife will
                  cover your hospital bill. The amount depends on the type of
                  hospital and class of ward.
                </p>
                <p>
                  2) Co-insurance (10% of the remaining bill) <br/> This is the amount
                  you have to pay on the claimable amount after the deductible.
                </p>
              </>
            )}
          </Tooltip>
        </span>
        <Info className="">
          <div>
            <span ref={redPercentage}></span>%
          </div>
          <InfoLabel>Singlife will pay</InfoLabel>
        </Info>
        <Info className={greenInfo}>
          <div>
            <span ref={greenPercentage}></span>%
          </div>
          <InfoLabel>You will pay</InfoLabel>
        </Info>

        <Chart rotationDeg={rotationRef}>
          <svg
            className="chartimg"
            width="100%"
            height="460"
            viewBox="0 0 450 426"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g className={chartGroup}>
              <g className={redChartGroup}>
                <path
                  className="redChart"
                  d="M162.059 425.611C132.922 418.005 105.697 404.391 82.1321 385.642C58.5673 366.894 39.1831 343.425 25.2238 316.743C11.2645 290.06 3.0386 260.754 1.07415 230.705C-0.890305 200.656 3.45009 170.528 13.8167 142.255C24.1833 113.982 40.3471 88.1898 61.2704 66.5328C82.1938 44.8758 107.414 27.8331 135.313 16.4986C163.212 5.1642 193.172 -0.211514 223.271 0.716597C253.37 1.64471 282.943 8.85613 310.09 21.8879L253.249 140.297C242.61 135.189 231.02 132.363 219.223 131.999C207.427 131.635 195.685 133.742 184.75 138.184C173.816 142.627 163.932 149.306 155.731 157.794C147.531 166.282 141.196 176.39 137.133 187.471C133.07 198.552 131.369 210.36 132.139 222.137C132.909 233.913 136.133 245.399 141.604 255.857C147.075 266.314 154.672 275.512 163.908 282.86C173.143 290.208 183.813 295.544 195.233 298.524L162.059 425.611Z"
                  fill="#FA8F8C"
                />
              </g>
              <g className={greenChartGroup}>
                <path
                  className="greenChart"
                  d="M433 216.999C433 176.857 421.814 137.509 400.697 103.37C379.58 69.2317 349.367 41.6529 313.449 23.7285L257 143C271.077 150.025 280.719 159.086 288.995 172.466C297.271 185.845 301.655 201.267 301.655 216.999L433 216.999Z"
                  fill="#C1EEEA"
                />
                <path
                  d="M433 217C433 186.8 426.667 156.936 414.41 129.335L294.369 182.642C299.173 193.46 301.655 205.164 301.655 217L433 217Z"
                  fill="#B2E3DF"
                />
                <path
                  d="M332.72 192H334.79C336.94 192 338.56 190.43 338.56 188.35C338.56 186.28 337.03 184.7 334.76 184.7H332.72V192ZM334.7 185.42C336.58 185.42 337.76 186.68 337.76 188.35C337.76 190.01 336.5 191.27 334.73 191.27H333.5V185.42H334.7ZM342.268 192.1C343.388 192.1 344.168 191.58 344.628 190.71L343.958 190.38C343.648 190.99 343.078 191.41 342.268 191.41C341.268 191.41 340.488 190.74 340.388 189.75H344.788V189.42C344.788 187.89 343.668 186.9 342.228 186.9C340.728 186.9 339.638 188.04 339.638 189.54C339.638 191.01 340.678 192.1 342.268 192.1ZM342.228 187.59C343.198 187.59 343.858 188.14 343.998 189.04H340.428C340.628 188.2 341.328 187.59 342.228 187.59ZM348.321 192.1C348.961 192.1 349.731 191.78 350.161 191.18V192H350.901V184.7H350.151V187.81C349.721 187.22 348.961 186.9 348.321 186.9C346.831 186.9 345.761 188.07 345.761 189.5C345.761 190.93 346.851 192.1 348.321 192.1ZM348.361 191.4C347.341 191.4 346.541 190.55 346.541 189.5C346.541 188.45 347.341 187.6 348.361 187.6C349.431 187.6 350.231 188.46 350.231 189.5C350.231 190.55 349.431 191.4 348.361 191.4ZM354.44 192.1C355.16 192.1 355.75 191.77 356.1 191.26V192H356.86V187H356.09V189.77C356.09 190.68 355.42 191.4 354.51 191.4C353.51 191.4 353.01 190.75 353.01 189.78V187H352.23V189.78C352.23 191.22 352.98 192.1 354.44 192.1ZM360.637 192.1C361.827 192.1 362.617 191.45 363.017 190.54L362.307 190.24C362.027 190.9 361.487 191.4 360.657 191.4C359.577 191.4 358.787 190.55 358.787 189.5C358.787 188.45 359.567 187.6 360.617 187.6C361.397 187.6 361.957 188 362.267 188.69L362.967 188.39C362.597 187.51 361.717 186.9 360.627 186.9C359.157 186.9 358.007 188 358.007 189.5C358.007 190.94 359.107 192.1 360.637 192.1ZM363.688 187.7H364.548V190.09C364.548 191.58 364.938 192 366.398 192H366.508V191.27C365.518 191.27 365.328 191.1 365.328 190.08V187.7H366.508V187H365.328V185.39L364.548 185.82V187H363.688V187.7ZM367.65 187V192H368.43V187H367.65ZM367.43 185.35C367.43 185.7 367.68 185.97 368.04 185.97C368.4 185.97 368.66 185.7 368.66 185.35C368.66 185 368.4 184.74 368.04 184.74C367.7 184.74 367.43 185 367.43 185.35ZM372.408 192.1C373.878 192.1 374.968 190.93 374.968 189.5C374.968 188.07 373.898 186.9 372.408 186.9C371.758 186.9 370.998 187.22 370.578 187.81V184.7H369.828V192H370.568V191.18C370.988 191.78 371.758 192.1 372.408 192.1ZM372.368 191.4C371.298 191.4 370.498 190.55 370.498 189.5C370.498 188.46 371.298 187.6 372.368 187.6C373.388 187.6 374.188 188.45 374.188 189.5C374.188 190.55 373.388 191.4 372.368 191.4ZM376.127 192H376.907V184.7H376.127V192ZM380.696 192.1C381.816 192.1 382.596 191.58 383.056 190.71L382.386 190.38C382.076 190.99 381.506 191.41 380.696 191.41C379.696 191.41 378.916 190.74 378.816 189.75H383.216V189.42C383.216 187.89 382.096 186.9 380.656 186.9C379.156 186.9 378.066 188.04 378.066 189.54C378.066 191.01 379.106 192.1 380.696 192.1ZM380.656 187.59C381.626 187.59 382.286 188.14 382.426 189.04H378.856C379.056 188.2 379.756 187.59 380.656 187.59ZM386.069 192.1C387.409 192.1 388.069 191.34 388.069 190.58C388.069 189.56 387.139 189.3 386.309 189.11C385.539 188.92 384.959 188.84 384.959 188.33C384.959 187.9 385.329 187.55 386.049 187.55C386.669 187.55 387.069 187.74 387.409 188.14L387.999 187.66C387.529 187.14 386.859 186.89 386.049 186.89C384.899 186.89 384.199 187.59 384.199 188.34C384.199 189.32 385.099 189.57 385.899 189.76C386.589 189.92 387.309 190.03 387.309 190.58C387.309 191.04 386.899 191.42 386.109 191.42C385.409 191.42 384.929 191.14 384.519 190.66L383.909 191.2C384.419 191.79 385.129 192.1 386.069 192.1ZM334.86 204.1C335.58 204.1 336.17 203.77 336.52 203.26V204H337.28V199H336.51V201.77C336.51 202.68 335.84 203.4 334.93 203.4C333.93 203.4 333.43 202.75 333.43 201.78V199H332.65V201.78C332.65 203.22 333.4 204.1 334.86 204.1ZM341.256 204.1C342.726 204.1 343.816 202.93 343.816 201.5C343.816 200.07 342.746 198.9 341.256 198.9C340.606 198.9 339.836 199.23 339.416 199.82V199H338.676V206.3H339.426V203.24C339.836 203.82 340.556 204.1 341.256 204.1ZM341.216 203.4C340.146 203.4 339.346 202.55 339.346 201.5C339.346 200.46 340.146 199.6 341.216 199.6C342.236 199.6 343.036 200.45 343.036 201.5C343.036 202.55 342.236 203.4 341.216 203.4ZM346.617 199.7H347.477V202.09C347.477 203.58 347.867 204 349.327 204H349.437V203.27C348.447 203.27 348.257 203.1 348.257 202.08V199.7H349.437V199H348.257V197.39L347.477 197.82V199H346.617V199.7ZM352.785 204.1C354.225 204.1 355.385 202.97 355.385 201.5C355.385 200.03 354.225 198.9 352.785 198.9C351.345 198.9 350.195 200.03 350.195 201.5C350.195 202.97 351.345 204.1 352.785 204.1ZM352.785 203.4C351.765 203.4 350.975 202.55 350.975 201.5C350.975 200.45 351.765 199.6 352.785 199.6C353.805 199.6 354.605 200.45 354.605 201.5C354.605 202.55 353.805 203.4 352.785 203.4ZM360.73 204.1C362.34 204.1 363.26 202.98 363.26 201.82C363.26 200.42 362.14 199.98 361.15 199.66C360.37 199.4 359.67 199.21 359.67 198.65C359.67 198.19 360.02 197.84 360.67 197.84C361.24 197.84 361.67 198.08 361.97 198.53L363.12 197.66C362.53 197.05 361.85 196.6 360.66 196.6C359.25 196.6 358.23 197.53 358.23 198.7C358.23 200.1 359.32 200.48 360.3 200.78C361.08 201.02 361.81 201.21 361.81 201.81C361.81 202.31 361.43 202.77 360.72 202.77C360.07 202.77 359.5 202.41 359.14 201.79L357.87 202.79C358.53 203.54 359.41 204.1 360.73 204.1ZM369.134 201.82C369.134 200.54 368.074 200.01 367.134 199.7V198.01C367.374 198.12 367.594 198.3 367.754 198.53L368.904 197.66C368.454 197.2 367.924 196.84 367.174 196.68V195.6H365.784V196.65C364.654 196.86 363.874 197.7 363.874 198.7C363.874 199.96 364.894 200.47 365.824 200.75V202.62C365.444 202.46 365.094 202.17 364.874 201.79L363.604 202.79C364.124 203.39 364.844 203.86 365.784 204.03V205.2H367.174V204.04C368.424 203.81 369.134 202.83 369.134 201.82ZM367.724 201.81C367.724 202.16 367.504 202.5 367.134 202.66V201.13C367.484 201.28 367.724 201.48 367.724 201.81ZM365.264 198.65C365.264 198.33 365.474 198.07 365.824 197.94V199.3C365.474 199.17 365.264 198.99 365.264 198.65ZM372.353 204.1C373.983 204.1 374.943 203.12 374.943 201.87C374.943 201.1 374.593 200.57 374.013 200.25C374.553 199.89 374.863 199.32 374.863 198.6C374.863 197.42 373.813 196.6 372.373 196.6C371.143 196.6 370.233 197.09 369.703 198.11L370.913 198.71C371.223 198.09 371.583 197.81 372.383 197.81C373.123 197.81 373.513 198.24 373.513 198.66C373.513 199.17 373.223 199.64 372.443 199.64H371.503V200.92H372.463C373.213 200.92 373.553 201.29 373.553 201.83C373.553 202.46 373.043 202.83 372.363 202.83C371.703 202.83 371.183 202.55 370.793 201.88L369.583 202.75C370.283 203.69 371.083 204.1 372.353 204.1ZM375.452 205.17H376.302L377.002 203.13H375.792L375.452 205.17ZM380.243 204.1C381.783 204.1 382.893 203 382.893 201.55C382.893 199.8 381.763 198.95 380.433 198.95C379.843 198.95 379.413 199.11 379.113 199.29L379.303 198H382.583V196.7H378.213L377.693 200.3L378.933 200.94C379.213 200.52 379.593 200.22 380.223 200.22C381.013 200.22 381.493 200.81 381.493 201.54C381.493 202.29 380.903 202.82 380.263 202.82C379.593 202.82 379.113 202.45 378.913 201.9L377.563 202.68C377.983 203.49 378.863 204.1 380.243 204.1ZM386.121 202.72C385.411 202.72 385.031 202.26 385.031 201.65V199.09C385.031 198.46 385.431 197.97 386.141 197.97C386.831 197.97 387.231 198.42 387.231 199.09V201.66C387.231 202.25 386.831 202.72 386.121 202.72ZM386.131 204.1C387.691 204.1 388.621 203.15 388.621 201.72V199.03C388.621 197.69 387.641 196.6 386.131 196.6C384.661 196.6 383.641 197.66 383.641 199.04V201.72C383.641 203.12 384.521 204.1 386.131 204.1ZM392 202.72C391.29 202.72 390.91 202.26 390.91 201.65V199.09C390.91 198.46 391.31 197.97 392.02 197.97C392.71 197.97 393.11 198.42 393.11 199.09V201.66C393.11 202.25 392.71 202.72 392 202.72ZM392.01 204.1C393.57 204.1 394.5 203.15 394.5 201.72V199.03C394.5 197.69 393.52 196.6 392.01 196.6C390.54 196.6 389.52 197.66 389.52 199.04V201.72C389.52 203.12 390.4 204.1 392.01 204.1Z"
                  fill="#13A498"
                />
                <path
                  d="M295 182L414 130"
                  stroke="#DBFBF8"
                  strokeWidth="2"
                  strokeDasharray="5 8"
                />
              </g>
            </g>
          </svg>
        </Chart>
      </ChartContainer>
    </>
  );
}
